/**
 * Common HTML element manipulation utilities.
 * Used by both WebsiteUpdater and HtmlStepperUpdater to apply changes to HTML content.
 */
import { AssetData, VideoAssetData } from '@superfunnel/types/asset-data';

// ============================================================================
// Types
// ============================================================================

export interface HtmlUpdateResult {
  success: boolean;
  html?: string;
  error?: string;
}

export interface AssetSettingsContent {
  type:
    | 'objectFit'
    | 'objectPosition'
    | 'openIn'
    | 'thumbnailUrl'
    | 'thumbnailAlt'
    | 'controls'
    | 'loop'
    | 'muted'
    | 'quality'
    | 'videoDeliveryMobile'
    | 'videoDeliveryTablet'
    | 'videoDeliveryDesktop'
    | 'priority'
    | 'sizes';
  value: string;
}

export type AssetType = 'image' | 'video';

/** Asset data for selecting/replacing elements */
export interface SelectAssetData {
  url?: string;
  src?: string;
  alt?: string;
  id?: string;
  type?: AssetType;
  /** Original tag name when asset type differs from element tag */
  originalTagName?: string;
  /** Thumbnail URL for video poster */
  thumbnailUrl?: string;
  /** Thumbnail alt text for accessibility */
  thumbnailAlt?: string;
}

// ============================================================================
// Core HTML Manipulation Helpers
// ============================================================================

/**
 * Parse an HTML fragment into a Document.
 *
 * The fragment is wrapped in an explicit `<body>` so the parser stays in the
 * "in body" insertion mode. Otherwise leading metadata content (top-level
 * `<style>`, `<link>`, `<meta>`, `<base>`, `<script>`) would be hoisted into
 * `<head>` per the HTML spec and dropped when we serialize `body.innerHTML`
 * via `getHtmlFromDoc`.
 *
 * Always pair this with `getHtmlFromDoc` when reading the result back so the
 * body-wrapping guarantee is preserved end to end.
 */
export function parseHtmlFragment(html: string): Document {
  const parser = new DOMParser();
  return parser.parseFromString(`<body>${html}</body>`, 'text/html');
}

/**
 * Parse HTML string into a Document and find an element by CSS selector path.
 */
export function parseHtmlAndFindElement(
  html: string,
  elementPath: string,
): { doc: Document; element: Element } | null {
  if (!html || !elementPath) {
    console.warn('[HTML_UTILS] Missing html or elementPath');
    return null;
  }

  try {
    const doc = parseHtmlFragment(html);
    const element = doc.querySelector(elementPath);

    if (!element) {
      console.warn(`[HTML_UTILS] Element not found for path: ${elementPath}`);
      return null;
    }

    return { doc, element };
  } catch (error) {
    console.error('[HTML_UTILS] Error parsing HTML:', error);
    return null;
  }
}

/**
 * Get the HTML output from a Document.
 *
 * The fragment must have been parsed via `parseHtmlFragment` (directly or
 * through `parseHtmlAndFindElement`), so all content — including section-level
 * `<style>` blocks — stays in the body and nothing is hoisted to `<head>`.
 */
export function getHtmlFromDoc(doc: Document): string {
  return doc.body.innerHTML;
}

// ============================================================================
// HTML Element Change
// ============================================================================

/**
 * Update the innerHTML of an element in HTML content.
 */
export function applyHtmlElementChangeToHtml(
  html: string,
  elementPath: string,
  newContent: string,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;
  element.innerHTML = newContent;

  return { success: true, html: getHtmlFromDoc(doc) };
}

// ============================================================================
// Asset Selection (supports img, video, svg switching)
// ============================================================================

/**
 * Get dimensions from an SVG element's viewBox attribute.
 * Returns width/height as strings suitable for element attributes.
 */
function getSvgDimensionsFromViewBox(element: Element): {
  width?: string;
  height?: string;
} {
  const viewBox = element.getAttribute('viewBox');
  if (!viewBox) return {};

  // viewBox format: "minX minY width height"
  const parts = viewBox.trim().split(/\s+/);
  if (parts.length >= 4) {
    return {
      width: parts[2],
      height: parts[3],
    };
  }
  return {};
}

/**
 * Copy common attributes from source element to target element.
 * Handles class, width, height, and derives dimensions from SVG viewBox.
 */
function copyElementAttributes(
  source: Element,
  target: Element,
  sourceTagName: string,
): void {
  // Copy class attribute
  const className = source.getAttribute('class');
  if (className) {
    target.setAttribute('class', className);
  }

  // Copy explicit width/height if present
  let width = source.getAttribute('width');
  let height = source.getAttribute('height');

  // For SVG, try to derive from viewBox if not explicitly set
  if (sourceTagName === 'svg') {
    const viewBoxDims = getSvgDimensionsFromViewBox(source);
    if (!width && viewBoxDims.width) {
      width = viewBoxDims.width;
    }
    if (!height && viewBoxDims.height) {
      height = viewBoxDims.height;
    }
  }

  if (width) {
    target.setAttribute('width', width);
  }
  if (height) {
    target.setAttribute('height', height);
  }

  // Copy style attribute (preserving object-fit, etc.)
  const style = source.getAttribute('style');
  if (style) {
    target.setAttribute('style', style);
  }
}

/**
 * Determine the target element tag based on asset type.
 */
function getTargetTagForAssetType(assetType: AssetType | undefined): string {
  switch (assetType) {
    case 'video':
      return 'video';
    case 'image':
    default:
      return 'img';
  }
}

/**
 * Update an image/video/svg element or replace it with a different element type.
 * Supports switching between img, video, and svg elements.
 */
export function applySelectAssetToHtml(
  html: string,
  elementPath: string,
  assetData: AssetData | SelectAssetData,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;
  const currentTagName = element.tagName.toLowerCase();
  const supportedTags = ['img', 'video', 'svg'];

  if (!supportedTags.includes(currentTagName)) {
    return {
      success: false,
      error: `Target element is not a supported asset type: ${currentTagName}`,
    };
  }

  // Support both AssetData (url) and simple object (src)
  const assetUrl =
    (assetData as AssetData).url || (assetData as SelectAssetData).src;
  if (!assetUrl) {
    return { success: false, error: 'No asset URL provided' };
  }

  const assetType = (assetData as SelectAssetData).type;
  const targetTagName = getTargetTagForAssetType(assetType);
  const needsTagSwitch = currentTagName !== targetTagName;

  if (needsTagSwitch) {
    // Create new element of the target type
    const newElement = doc.createElement(targetTagName);

    // Copy attributes from original element
    copyElementAttributes(element, newElement, currentTagName);

    // Set the source based on target type
    if (targetTagName === 'video') {
      newElement.setAttribute('src', assetUrl);
      // Set sensible defaults for video
      newElement.setAttribute('playsinline', '');
      // Default to controls enabled
      newElement.setAttribute('controls', 'true');
      // Remove data-open-in if switching from video to video
      newElement.removeAttribute('data-open-in');

      // Set thumbnail/poster if available (from VideoAssetData.thumbnailImage or SelectAssetData.thumbnailUrl)
      const thumbnailUrl =
        (assetData as VideoAssetData).thumbnailImage?.url ||
        (assetData as SelectAssetData).thumbnailUrl;
      if (thumbnailUrl) {
        newElement.setAttribute('poster', thumbnailUrl);
        newElement.setAttribute('data-thumbnail-url', thumbnailUrl);

        const thumbnailAlt =
          (assetData as VideoAssetData).thumbnailImage?.alt ||
          (assetData as SelectAssetData).thumbnailAlt;
        if (thumbnailAlt) {
          newElement.setAttribute('data-thumbnail-alt', thumbnailAlt);
        }
      }
    } else {
      // img
      newElement.setAttribute('src', assetUrl);
      newElement.removeAttribute('controls');
      newElement.removeAttribute('autoplay');
      newElement.removeAttribute('muted');
      newElement.removeAttribute('loop');
      newElement.removeAttribute('playsinline');
      newElement.removeAttribute('data-open-in');
      newElement.removeAttribute('poster');
      newElement.removeAttribute('data-thumbnail-url');
      newElement.removeAttribute('data-thumbnail-alt');

      // When switching to img without explicit dimensions, add default dimensions
      // This is required for Next.js Image to work without fill mode
      // (fill mode requires the parent to have position: relative which we can't guarantee)
      const hasWidth = newElement.hasAttribute('width');
      const hasHeight = newElement.hasAttribute('height');
      if (!hasWidth || !hasHeight) {
        // Use 16:9 aspect ratio defaults (common for videos)
        if (!hasWidth) {
          newElement.setAttribute('width', '1280');
        }
        if (!hasHeight) {
          newElement.setAttribute('height', '720');
        }

        // Ensure object-fit: cover is set so the image fills properly
        const existingStyle = newElement.getAttribute('style') || '';
        const styleProps = parseStyleAttribute(existingStyle);
        if (!styleProps['object-fit']) {
          styleProps['object-fit'] = 'cover';
        }
        newElement.setAttribute('style', serializeStyleAttribute(styleProps));
      }
    }

    // Set alt text if provided
    const alt = (assetData as SelectAssetData).alt;
    if (alt) {
      if (targetTagName === 'img') {
        newElement.setAttribute('alt', alt);
      }
    }

    // Set asset tracking attributes
    newElement.setAttribute('data-asset-type', assetType || 'image');

    const assetId = (assetData as SelectAssetData).id;
    if (assetId) {
      newElement.setAttribute('data-asset-id', assetId);
    }

    // Replace the element in DOM
    element.parentNode?.replaceChild(newElement, element);
  } else {
    // Same element type - just update attributes
    if (currentTagName === 'video') {
      // Check for <source> child element
      const sourceEl = element.querySelector('source');
      if (sourceEl) {
        sourceEl.setAttribute('src', assetUrl);
      } else {
        element.setAttribute('src', assetUrl);
      }

      const thumbnailUrl = (assetData as VideoAssetData)?.thumbnailImage?.url;
      if (thumbnailUrl) {
        element.setAttribute('data-thumbnail-url', thumbnailUrl);
        element.setAttribute('poster', thumbnailUrl);

        const thumbnailAlt = (assetData as VideoAssetData).thumbnailImage?.alt;
        if (thumbnailAlt) {
          element.setAttribute('data-thumbnail-alt', thumbnailAlt);
        } else {
          element.removeAttribute('data-thumbnail-alt');
        }
      }
    } else {
      element.setAttribute('src', assetUrl);
    }

    // Update alt text if provided (for img only)
    const alt = (assetData as SelectAssetData).alt;
    if (alt && currentTagName === 'img') {
      element.setAttribute('alt', alt);
    }

    // Set asset tracking attributes
    if (assetType) {
      element.setAttribute('data-asset-type', assetType);
    }

    const assetId = (assetData as SelectAssetData).id;
    if (assetId) {
      element.setAttribute('data-asset-id', assetId);
    }
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}

/**
 * Data for selecting a thumbnail image for a video element.
 */
export interface SelectThumbnailData {
  url?: string;
  src?: string;
  alt?: string;
}

/**
 * Apply a thumbnail image to a video element.
 * Sets data-thumbnail-url, data-thumbnail-alt, and poster attributes.
 */
export function applySelectThumbnailToHtml(
  html: string,
  elementPath: string,
  thumbnailData: SelectThumbnailData,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;
  const tagName = element.tagName.toLowerCase();

  if (tagName !== 'video') {
    return {
      success: false,
      error: `Target element is not a video: ${tagName}`,
    };
  }

  // Support both url and src properties
  const thumbnailUrl = thumbnailData.url || thumbnailData.src;
  if (!thumbnailUrl) {
    return { success: false, error: 'No thumbnail URL provided' };
  }

  // Set the thumbnail URL on data attribute and poster
  element.setAttribute('data-thumbnail-url', thumbnailUrl);
  element.setAttribute('poster', thumbnailUrl);

  // Set alt text if provided
  if (thumbnailData.alt) {
    element.setAttribute('data-thumbnail-alt', thumbnailData.alt);
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}

// ============================================================================
// Asset Settings (objectFit, objectPosition, openIn, thumbnail)
// ============================================================================

/**
 * Parse style attribute string into an object.
 */
function parseStyleAttribute(style: string): Record<string, string> {
  return style
    .split(';')
    .filter((prop) => prop.trim())
    .reduce((acc: Record<string, string>, prop) => {
      const colonIndex = prop.indexOf(':');
      if (colonIndex > 0) {
        const key = prop.substring(0, colonIndex).trim();
        const value = prop.substring(colonIndex + 1).trim();
        if (key && value) acc[key] = value;
      }
      return acc;
    }, {});
}

/**
 * Serialize style object back to string.
 */
function serializeStyleAttribute(styleObj: Record<string, string>): string {
  return Object.entries(styleObj)
    .map(([key, value]) => `${key}: ${value}`)
    .join('; ');
}

/**
 * Apply asset settings to an element.
 * Supports both image settings (objectFit, objectPosition) and video settings (openIn, thumbnail).
 */
export function applyAssetSettingsToHtml(
  html: string,
  elementPath: string,
  settings: AssetSettingsContent,
): HtmlUpdateResult {
  const result = parseHtmlAndFindElement(html, elementPath);
  if (!result) {
    return { success: false, error: `Element not found: ${elementPath}` };
  }

  const { doc, element } = result;
  const tagName = element.tagName.toLowerCase();
  const supportedTags = ['img', 'video', 'svg'];

  if (!supportedTags.includes(tagName)) {
    return {
      success: false,
      error: `Target element is not a supported asset type: ${tagName}`,
    };
  }

  const { type, value } = settings;

  // Handle data-attribute settings applicable to both images and videos
  if (type === 'quality') {
    if (value && value !== '70') {
      element.setAttribute('data-quality', value);
    } else {
      element.removeAttribute('data-quality');
    }
    return { success: true, html: getHtmlFromDoc(doc) };
  }

  if (type === 'priority') {
    // Remove both forms to avoid conflicts
    element.removeAttribute('priority');
    if (value === 'true') {
      element.setAttribute('data-priority', 'true');
    } else {
      element.removeAttribute('data-priority');
    }
    return { success: true, html: getHtmlFromDoc(doc) };
  }

  if (type === 'sizes') {
    if (value) {
      element.setAttribute('sizes', value);
    } else {
      element.removeAttribute('sizes');
    }
    return { success: true, html: getHtmlFromDoc(doc) };
  }

  // Handle style-based settings (objectFit, objectPosition)
  if (type === 'objectFit' || type === 'objectPosition') {
    const existingStyle = element.getAttribute('style') || '';
    const styleProps = parseStyleAttribute(existingStyle);

    switch (type) {
      case 'objectFit':
        if (value === 'default') {
          delete styleProps['object-fit'];
        } else {
          styleProps['object-fit'] = value;
        }
        break;

      case 'objectPosition':
        if (value === 'default') {
          delete styleProps['object-position'];
        } else {
          styleProps['object-position'] = value;
        }
        break;
    }

    // Update or remove style attribute
    const newStyle = serializeStyleAttribute(styleProps);
    if (newStyle) {
      element.setAttribute('style', newStyle);
    } else {
      element.removeAttribute('style');
    }

    return { success: true, html: getHtmlFromDoc(doc) };
  }

  // Handle video-specific settings (openIn, thumbnailUrl, thumbnailAlt)
  if (tagName !== 'video') {
    return {
      success: false,
      error: `Setting '${type}' is only applicable to video elements`,
    };
  }

  switch (type) {
    case 'openIn':
      // openIn values: 'autoplay' | 'hover' | 'modal' | undefined (click-to-play)
      if (value && value !== 'click') {
        element.setAttribute('data-open-in', value);
      } else {
        element.removeAttribute('data-open-in');
      }
      break;

    case 'thumbnailUrl':
      if (value) {
        element.setAttribute('data-thumbnail-url', value);
        // Also set poster for native browser support
        element.setAttribute('poster', value);
      } else {
        element.removeAttribute('data-thumbnail-url');
        element.removeAttribute('poster');
      }
      break;

    case 'thumbnailAlt':
      if (value) {
        element.setAttribute('data-thumbnail-alt', value);
      } else {
        element.removeAttribute('data-thumbnail-alt');
      }
      break;

    case 'controls':
    case 'loop':
    case 'muted':
      // Boolean attributes: 'auto' removes the attribute, 'true' sets it, 'false' sets it to "false"
      if (value === 'auto') {
        element.removeAttribute(type);
      } else if (value === 'true') {
        element.setAttribute(type, '');
      } else if (value === 'false') {
        element.setAttribute(type, 'false');
      }
      break;

    case 'videoDeliveryMobile':
    case 'videoDeliveryTablet':
    case 'videoDeliveryDesktop': {
      const attrName =
        type === 'videoDeliveryMobile'
          ? 'data-video-delivery-mobile'
          : type === 'videoDeliveryTablet'
            ? 'data-video-delivery-tablet'
            : 'data-video-delivery-desktop';

      if (value && value !== 'auto') {
        element.setAttribute(attrName, value);
      } else {
        element.removeAttribute(attrName);
      }
      break;
    }

    default:
      return { success: false, error: `Unknown asset setting type: ${type}` };
  }

  return { success: true, html: getHtmlFromDoc(doc) };
}
